import { useState, useEffect, useCallback } from "react";
import { Bell, Swords, Users, MessageSquare, AlertTriangle, X, Check, Video, Repeat2, Phone, Inbox } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface Notification {
  id: string;
  type: "debate_invite" | "matchmaking" | "community_reply" | "video_reply" | "repost" | "system" | "sms_inbound" | "call_inbound";
  title: string;
  message: string;
  read: boolean;
  timestamp: Date;
  actionUrl?: string;
}

const typeConfig = {
  debate_invite: { icon: Swords, color: "text-sovereign-gold", bg: "bg-sovereign-gold/10" },
  matchmaking: { icon: Users, color: "text-ai-cyan", bg: "bg-ai-cyan/10" },
  community_reply: { icon: MessageSquare, color: "text-tactical-green", bg: "bg-tactical-green/10" },
  video_reply: { icon: Video, color: "text-ai-cyan", bg: "bg-ai-cyan/10" },
  repost: { icon: Repeat2, color: "text-tactical-green", bg: "bg-tactical-green/10" },
  sms_inbound: { icon: Inbox, color: "text-ai-cyan", bg: "bg-ai-cyan/10" },
  call_inbound: { icon: Phone, color: "text-sovereign-gold", bg: "bg-sovereign-gold/10" },
  system: { icon: AlertTriangle, color: "text-tactical-amber", bg: "bg-tactical-amber/10" },
};

const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);

  // Seed initial notifications + listen for realtime community replies
  useEffect(() => {
    if (!user) return;

    // Seed some contextual notifications
    setNotifications([
      { id: "1", type: "system", title: "Platform Update", message: "FedRAMP Evidence Pack v2 is now available for download.", read: false, timestamp: new Date(Date.now() - 120000), actionUrl: "/trust-center" },
      { id: "2", type: "matchmaking", title: "Queue Update", message: "3 debaters are waiting in the Arena lobby matching your skill range.", read: false, timestamp: new Date(Date.now() - 300000), actionUrl: "/debate-lobby" },
      { id: "3", type: "debate_invite", title: "Challenge Received", message: "GovAdvisor_TX challenged you on Border Policy.", read: false, timestamp: new Date(Date.now() - 600000), actionUrl: "/arena" },
    ]);

    // Listen for new community replies on user's posts
    const channel = supabase
      .channel("user-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "post_replies" },
        (payload) => {
          const reply = payload.new as any;
          if (reply.user_id !== user.id) {
            setNotifications((prev) => [
              {
                id: `reply-${reply.id}`,
                type: "community_reply",
                title: "New Reply",
                message: reply.content?.slice(0, 80) + (reply.content?.length > 80 ? "…" : ""),
                read: false,
                timestamp: new Date(),
                actionUrl: "/community",
              },
              ...prev,
            ]);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "matchmaking_queue" },
        (payload) => {
          const entry = payload.new as any;
          if (entry.user_id === user.id && entry.status === "matched") {
            setNotifications((prev) => [
              {
                id: `match-${entry.id}`,
                type: "matchmaking",
                title: "Match Found!",
                message: "You've been paired with an opponent. Head to the Arena now.",
                read: false,
                timestamp: new Date(),
                actionUrl: "/arena",
              },
              ...prev,
            ]);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "video_comments" },
        (payload) => {
          const vc = payload.new as any;
          // Only notify if it's a reply (has parent_id) and not from the current user
          if (vc.parent_id && vc.user_id !== user.id) {
            setNotifications((prev) => [
              {
                id: `vc-${vc.id}`,
                type: "video_reply",
                title: "New Video Reply",
                message: vc.content?.slice(0, 80) + (vc.content?.length > 80 ? "…" : ""),
                read: false,
                timestamp: new Date(),
                actionUrl: "/video-hub",
              },
              ...prev,
            ]);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "post_reposts" },
        async (payload) => {
          const repost = payload.new as any;
          if (repost.user_id !== user.id) {
            // Check if the reposted post belongs to the current user
            const { data: post } = await supabase
              .from("community_posts")
              .select("user_id, content")
              .eq("id", repost.post_id)
              .maybeSingle();
            if (post && post.user_id === user.id) {
              setNotifications((prev) => [
                {
                  id: `repost-${repost.id}`,
                  type: "repost",
                  title: "Post Reposted",
                  message: repost.quote
                    ? `Quote: "${repost.quote.slice(0, 60)}${repost.quote.length > 60 ? "…" : ""}"`
                    : `Your post was reposted: "${post.content?.slice(0, 60)}…"`,
                  read: false,
                  timestamp: new Date(),
                  actionUrl: "/community",
                },
                ...prev,
              ]);
            }
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "ceo_pilot_messages", filter: `direction=eq.inbound` },
        (payload) => {
          const msg = payload.new as any;
          if (msg.user_id === user.id) {
            setNotifications((prev) => [
              {
                id: `sms-${msg.id}`,
                type: "sms_inbound",
                title: "New SMS from " + (msg.contact_name || "Unknown"),
                message: msg.body?.slice(0, 80) + (msg.body?.length > 80 ? "…" : ""),
                read: false,
                timestamp: new Date(),
                actionUrl: "/ceo-pilot",
              },
              ...prev,
            ]);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "autopilot_call_logs" },
        (payload) => {
          const call = payload.new as any;
          if (call.user_id === user.id) {
            setNotifications((prev) => [
              {
                id: `call-${call.id}`,
                type: "call_inbound",
                title: "New Call from " + (call.caller_name || "Unknown"),
                message: call.summary?.slice(0, 80) || "New call received",
                read: false,
                timestamp: new Date(),
                actionUrl: "/ceo-pilot",
              },
              ...prev,
            ]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const markRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const timeAgo = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative text-muted-foreground hover:text-foreground transition-colors p-1">
          <Bell className="w-4 h-4" />
          <AnimatePresence>
            {unreadCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-sovereign-gold text-primary-foreground text-[9px] font-bold flex items-center justify-center"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-card border-border" align="end" sideOffset={8}>
        <div className="flex items-center justify-between p-3 border-b border-border">
          <h3 className="text-xs font-mono uppercase tracking-widest text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-[10px] font-mono text-sovereign-gold hover:text-sovereign-gold/80 transition-colors flex items-center gap-1">
              <Check className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">No notifications</p>
            </div>
          ) : (
            notifications.map((notif) => {
              const config = typeConfig[notif.type];
              const Icon = config.icon;
              return (
                <motion.div
                  key={notif.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`flex items-start gap-3 p-3 border-b border-border/50 hover:bg-secondary/50 transition-colors cursor-pointer ${!notif.read ? "bg-secondary/30" : ""}`}
                  onClick={() => {
                    markRead(notif.id);
                    if (notif.actionUrl) {
                      window.location.href = notif.actionUrl;
                      setOpen(false);
                    }
                  }}
                >
                  <div className={`w-7 h-7 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-xs font-semibold truncate">{notif.title}</h4>
                      <button
                        onClick={(e) => { e.stopPropagation(); dismissNotification(notif.id); }}
                        className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                    <span className="text-[9px] text-muted-foreground/60 font-mono mt-1 block">{timeAgo(notif.timestamp)}</span>
                  </div>
                  {!notif.read && <div className="w-1.5 h-1.5 rounded-full bg-sovereign-gold flex-shrink-0 mt-2" />}
                </motion.div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
